/**
 * Type definitions for Outbound Auth service responses
 *
 * These types mirror the response models from the outbound-auth service.
 * Source of truth: https://bitbucket.org/atlassian/outbound-auth/
 * Reference implementation: /src/service/accountManagement.ts
 * API Spec: /docs/content/platform/outbound-auth/swagger.yaml
 */

/**
 * Include fields for controlling what data is returned in account responses
 * Mirrors AtlassianAuthAccount.IncludeFields from outbound-auth service
 */
export enum IncludeFields {
	AccessToken = 'accessToken',
	DisplayName = 'displayName',
	AaId = 'aaId',
	AvatarUrl = 'avatarUrl',
	ExternalAccountId = 'externalAccountId',
	Expiry = 'expiry',
}

/**
 * Set of include fields for controlling response data
 * Mirrors AtlassianAuthAccount.IncludeSet from outbound-auth service
 */
export type IncludeSet = Set<IncludeFields>;

/**
 * OAuth Service Account
 * Represents a user's connected account for a specific OAuth service
 * Mirrors AtlassianAuthAccount.Json from outbound-auth service
 */
export type OAuthServiceAccount = {
	id: string;
	status: OAuthServiceAccountStatus;
	connectedAt?: string;
	scopes?: string[];
	grantedScopes?: string[];
	contextAris?: string[];
	aaId?: string;
	accessToken?: string;
	expiry?: string;
	displayName?: string;
	avatarUrl?: string;
	externalAccountId?: string;
	statusMetadata?: string;
};

export type OAuthServiceAccountStatus = 'available' | 'expired' | 'refreshNeeded' | 'serverError';
/**
 * OAuth Service definition
 * Mirrors AuthService properties from outbound-auth service
 */
export type OAuthService = {
	containerId?: string;
	serviceKey: string;
	version?: string;
	displayName: string;
	authType?: string;
	providerType: string;
};

export type OAuthContainer = {
	containerId: string;
	displayName: string;
};

/**
 * Service with its associated accounts
 * Mirrors GetAccountsServiceInfo from outbound-auth service
 */
export type AccountsServiceInfo = {
	service: OAuthService;
	accounts: OAuthServiceAccount[];
};

export type Container = {
	container: OAuthContainer;
	services: AccountsServiceInfo[];
};

/**
 * Response from outbound-auth service for GetAccountsByAaIDAndContainerIdResponse
 * Mirrors GetAccountsByAaIDAndContainerIdResponse from outbound-auth service
 * @see https://bitbucket.org/atlassian/outbound-auth/src/master/src/service/accountManagement.ts
 * @see https://bitbucket.org/atlassian/outbound-auth/src/master/docs/content/platform/outbound-auth/swagger.yaml
 */
export type OutboundAuthContainerResponse = {
	services: AccountsServiceInfo[];
};

/**
 * Response from outbound-auth service for GetAccountsByAaIDResponse
 * Mirrors GetAccountsByAaIDResponse from outbound-auth service
 * Returns all containers and their services/accounts for a given Atlassian Account ID
 * @see https://bitbucket.org/atlassian/outbound-auth/src/master/src/service/accountManagement.ts (line 4446)
 * @see https://bitbucket.org/atlassian/outbound-auth/src/master/src/route/getAccountsForAaID.ts
 */
export type GetAccountsByAaIDResponse = {
	containers: Container[];
};

export class OutboundAuthApiError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'OutboundAuthApiError';
	}
}
